import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { SettlementPanel } from '../../screens/meta/SettlementPanel.tsx';
import { Badge, Button, Panel } from '../../ui/index.ts';
import { clock } from '../format.ts';
import { useGame } from '../GameContext.tsx';
import { Newsreel } from '../fx/Newsreel.tsx';
import { useGameStore } from '../gameStore.ts';
import { TeamList } from '../hud/TeamList.tsx';
import styles from './Reveal.module.css';

/** The newsreel (GDD §5.7): how it ended, who the infiltrator was, what each event really was. */
export function Reveal() {
  const { t } = useTranslation('game');
  const { view, g, client } = useGame();
  const navigate = useNavigate();
  const setup = useGameStore((s) => s.setup);
  const start = useGameStore((s) => s.start);
  const leave = useGameStore((s) => s.leave);
  const home = useGameStore((s) => s.home);
  const result = view.result;
  if (!result) {
    return (
      <Panel tone="night">
        <p role="status">{t('reveal.waiting')}</p>
      </Panel>
    );
  }
  const name = (id: string | null): string => view.players.find((p) => p.id === id)?.name ?? '?';
  const iWon = result.winners.includes(view.you.id);
  // The headline of the reel: what the inspector found.
  const headline = result.infiltrator
    ? result.expelled === result.infiltrator
      ? t('reveal.headline.moleCaught', { name: name(result.infiltrator) })
      : result.expelled
        ? t('reveal.headline.innocentOut', { name: name(result.expelled) })
        : t('reveal.headline.moleFree', { name: name(result.infiltrator) })
    : result.expelled
      ? t('reveal.headline.innocentOut', { name: name(result.expelled) })
      : t('reveal.headline.noMole');

  const timeline = view.timeline.flatMap((e) => {
    const secret = e.secret;
    if (e.type === 'notice' && secret) {
      const sabotage = secret['cause'] === 'sabotage';
      return [
        {
          id: e.id,
          at: e.atMs,
          text: sabotage
            ? t('reveal.sabotage', {
                who: name(String(secret['by'])),
                what: g(`events.notice.${String(e.data['kind'])}`),
              })
            : t('reveal.chance', { what: g(`events.notice.${String(e.data['kind'])}`) }),
          tone: sabotage ? 'sabotage' : 'chance',
        },
      ];
    }
    return [];
  });

  return (
    <div className={styles['layout']}>
      <Newsreel title={t('reveal.reel')} className={styles['headline']}>
        <p className={styles['reelHeadline']}>{headline}</p>
        <p className={styles['verdict']}>
          {result.success ? t('reveal.success') : t(`reveal.failed.${result.failure ?? 'time'}`)}
        </p>
        <p className={iWon ? styles['won'] : styles['lost']}>
          {iWon ? t('reveal.youWon') : t('reveal.youLost')}
        </p>
        <p>
          {result.infiltrator
            ? t('reveal.infiltrator', { name: name(result.infiltrator) })
            : t('reveal.noInfiltrator')}
          {result.expelled ? ` ${t('reveal.expelled', { name: name(result.expelled) })}` : ''}
        </p>
      </Newsreel>

      <Panel tone="night" title={t('reveal.loot')}>
        {result.success ? (
          <dl className={styles['facts']}>
            <dt>{t('reveal.base')}</dt>
            <dd>{result.lootBase}</dd>
            <dt>{t('reveal.extras')}</dt>
            <dd>{result.lootExtras}</dd>
            {result.penaltyPercent > 0 ? (
              <>
                <dt>{t('reveal.penalty', { alarm: Math.floor(result.alarmFinal / 10) })}</dt>
                <dd>{`−${result.penaltyPercent}%`}</dd>
              </>
            ) : null}
            <dt>{t('reveal.total')}</dt>
            <dd>
              <strong>{result.loot}</strong>
            </dd>
          </dl>
        ) : (
          <p>{t('reveal.nothing', { planned: result.plannedLoot })}</p>
        )}
      </Panel>

      <Panel tone="night" title={t('reveal.scores')}>
        <ul className={styles['scores']}>
          {view.players.map((p) => (
            <li key={p.id}>
              <span className={styles['who']}>{p.name}</span>
              <Badge tone="neutral">{g(`roles.${p.role}`)}</Badge>
              {p.id === result.infiltrator ? <Badge tone="pink">{t('reveal.mole')}</Badge> : null}
              {result.winners.includes(p.id) ? (
                <Badge tone="green">{t('reveal.winner')}</Badge>
              ) : null}
              <span className={styles['amount']}>
                {result.scores[p.id] !== undefined
                  ? t('reveal.score', { amount: result.scores[p.id] })
                  : '—'}
              </span>
            </li>
          ))}
        </ul>
      </Panel>

      {client.mode === 'online' ? <SettlementPanel /> : null}

      <Panel tone="night" title={t('reveal.timeline')}>
        {timeline.length === 0 ? (
          <p className={styles['muted']}>{t('reveal.noEvents')}</p>
        ) : (
          <ul className={styles['timeline']}>
            {timeline.map((e) => (
              <li key={e.id} className={styles[e.tone]}>
                <span className={styles['time']}>{clock(e.at)}</span>{' '}
                <span className={styles['entry']}>
                  {e.text}
                  {e.tone === 'sabotage' ? (
                    <svg
                      className={styles['marker']}
                      viewBox="0 0 100 40"
                      preserveAspectRatio="none"
                      aria-hidden="true"
                    >
                      <ellipse cx="50" cy="20" rx="47" ry="17" pathLength={1} />
                    </svg>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        )}
        <div className={styles['team']}>
          <TeamList compact />
        </div>
      </Panel>

      <div className={styles['actions']}>
        {setup ? (
          <Button
            icon="play"
            onClick={() => {
              const next = { ...setup };
              delete next.seed;
              void start(next);
            }}
          >
            {t('reveal.again')}
          </Button>
        ) : null}
        <Button
          variant="secondary"
          onClick={() => {
            client.dispose();
            leave();
            void navigate(client.chapter ? '/play/tutorial' : home);
          }}
        >
          {t('reveal.menu')}
        </Button>
      </div>
    </div>
  );
}
